/**
 * NAT-2430 — the FE half of the metric registry.
 *
 * The BE table is deliberately open: a new measurement is an INSERT plus an
 * entry in `src/services/assistant/metrics.ts`. This file is what lets the same
 * be true here — a new tile appears by adding a row below, never by editing the
 * view.
 *
 * The keys must match the BE's `METRICS` exactly. They are duplicated rather
 * than shared because the two repositories deploy separately: a FE that asks
 * for a metric the deployed BE does not have yet gets it dropped from the
 * response rather than erroring, which is the behaviour that makes a
 * BE-then-FE deploy order safe.
 */

export type MetricGroup = 'volume' | 'health' | 'quality' | 'cost' | 'performance';

export type MetricFormat = 'count' | 'tokens' | 'ms';

export interface MetricSpec {
  /** Must match a value in the BE `METRICS` registry. */
  key: string;
  /** i18n key under `dashboard.assistantOverviewPage`. */
  labelKey: string;
  group: MetricGroup;
  format: MetricFormat;
  /**
   * Render as a percentage of this metric instead of as a total.
   *
   * The ratio is computed here, from the two stored series, because the BE
   * deliberately stores numerators and denominators and never rates — a stored
   * rate cannot be re-aggregated over a window longer than the one it was
   * computed for.
   */
  ratioOf?: string;
  /**
   * How the window's daily points collapse to one number.
   *
   * `sum`  — a count of things that happened, which is additive.
   * `last` — a level, such as a latency percentile, where a sum is meaningless.
   *          Reads the most recent day that was actually measured, skipping
   *          nulls, because an unmeasured day is not a fast one.
   * `mean` — a per-day DISTINCT count, which is the one thing that must never be
   *          summed: `users` and `conversations` are daily distincts, so adding
   *          them across a window yields user-days, not people. One person
   *          active every day for a month would read as 30. There is no way to
   *          recover a window-level distinct count from daily ones, so the tile
   *          reports the honest daily average instead of a wrong total.
   */
  aggregate: 'sum' | 'last' | 'mean';

  /**
   * Render as this metric divided by another, in the metric's own unit.
   *
   * Distinct from `ratioOf`, which renders a percentage. Used for
   * `latency_sum / calls` — the only latency figure that IS aggregatable across
   * a window, since a p95 cannot be averaged with another p95.
   */
  quotientOf?: string;
  /** Lower is better — drives the tile's accent only, never a hard threshold. */
  inverse?: boolean;
}

export const METRIC_SPECS: MetricSpec[] = [
  // --- Volume ---
  {
    key: 'questions',
    labelKey: 'metric_questions',
    group: 'volume',
    format: 'count',
    aggregate: 'sum',
  },
  {
    key: 'answers',
    labelKey: 'metric_answers',
    group: 'volume',
    format: 'count',
    aggregate: 'sum',
  },
  {
    key: 'conversations',
    labelKey: 'metric_conversations',
    group: 'volume',
    format: 'count',
    aggregate: 'mean',
  },
  { key: 'users', labelKey: 'metric_users', group: 'volume', format: 'count', aggregate: 'mean' },

  // --- Health --- (all as a share of answers: 12 unserved means nothing without the total)
  {
    key: 'unserved',
    labelKey: 'metric_unserved',
    group: 'health',
    format: 'count',
    ratioOf: 'answers',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'widened',
    labelKey: 'metric_widened',
    group: 'health',
    format: 'count',
    ratioOf: 'answers',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'truncated',
    labelKey: 'metric_truncated',
    group: 'health',
    format: 'count',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'refused',
    labelKey: 'metric_refused',
    group: 'health',
    format: 'count',
    aggregate: 'sum',
    inverse: true,
  },

  // --- Quality ---
  {
    key: 'rated',
    labelKey: 'metric_coverage',
    group: 'quality',
    format: 'count',
    ratioOf: 'answers',
    aggregate: 'sum',
  },
  {
    key: 'thumbs_up',
    labelKey: 'metric_thumbs_up',
    group: 'quality',
    format: 'count',
    aggregate: 'sum',
  },
  {
    key: 'thumbs_down',
    labelKey: 'metric_thumbs_down',
    group: 'quality',
    format: 'count',
    aggregate: 'sum',
    inverse: true,
  },

  // --- Cost --- (tokens, never a currency: the BE stores facts, not a price list)
  {
    key: 'tokens_input',
    labelKey: 'metric_tokens_input',
    group: 'cost',
    format: 'tokens',
    aggregate: 'sum',
  },
  {
    key: 'tokens_cached',
    labelKey: 'metric_tokens_cached',
    group: 'cost',
    format: 'tokens',
    ratioOf: 'tokens_input',
    aggregate: 'sum',
  },
  {
    key: 'tokens_output',
    labelKey: 'metric_tokens_output',
    group: 'cost',
    format: 'tokens',
    aggregate: 'sum',
  },
  {
    key: 'tokens_reasoning',
    labelKey: 'metric_tokens_reasoning',
    group: 'cost',
    format: 'tokens',
    ratioOf: 'tokens_output',
    aggregate: 'sum',
  },

  // --- Performance ---
  {
    key: 'calls',
    labelKey: 'metric_calls',
    group: 'performance',
    format: 'count',
    aggregate: 'sum',
  },
  {
    // The one latency figure that survives aggregation: a p95 cannot be averaged
    // with another p95, but a total over a count can. This is also what gives
    // the stored `latency_sum` a reader.
    key: 'latency_sum',
    labelKey: 'metric_latency_mean',
    group: 'performance',
    format: 'ms',
    quotientOf: 'calls',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'latency_p50',
    labelKey: 'metric_latency_p50',
    group: 'performance',
    format: 'ms',
    aggregate: 'last',
    inverse: true,
  },
  {
    key: 'latency_p95',
    labelKey: 'metric_latency_p95',
    group: 'performance',
    format: 'ms',
    aggregate: 'last',
    inverse: true,
  },
  {
    key: 'fallbacks',
    labelKey: 'metric_fallbacks',
    group: 'performance',
    format: 'count',
    ratioOf: 'calls',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'repairs',
    labelKey: 'metric_repairs',
    group: 'performance',
    format: 'count',
    ratioOf: 'calls',
    aggregate: 'sum',
    inverse: true,
  },
  {
    key: 'calls_failed',
    labelKey: 'metric_calls_failed',
    group: 'performance',
    format: 'count',
    ratioOf: 'calls',
    aggregate: 'sum',
    inverse: true,
  },
];

export const METRIC_GROUPS: MetricGroup[] = ['volume', 'health', 'quality', 'cost', 'performance'];

/** Every metric key the page asks the BE for, including ratio denominators. */
export const REQUESTED_METRICS = Array.from(
  new Set(
    METRIC_SPECS.flatMap((s) =>
      [s.key, s.ratioOf, s.quotientOf].filter((k): k is string => Boolean(k)),
    ),
  ),
);

/**
 * The windows offered by the period toggle.
 *
 * 90 is the last one that can ever be complete from a standing start: the raw
 * messages behind the rollup are purged at 90 days, so before the rollup has
 * been running longer than that, a wider window shows real zeros rather than
 * missing data. Longer windows become meaningful as the table accumulates.
 */
export const PERIOD_OPTIONS = [7, 30, 90] as const;
